import { useForm, FormProvider, Controller } from "react-hook-form";
import {
  ALL_REGIONS_ID,
  CreatingSearchHHForm,
  creatingSearchHHSchema,
  getAreaId,
  getNumericalValue,
} from "./constans";
import {
  crossImg,
  openImg,
  SearchRegionField,
} from "./SearchRegionField/SearchRegionField";
import { SalaryRangeAccordion } from "./SalaryRangeAccordion/SalaryRangeAccordion";
import { AdditionalParamsField } from "./AdditionalParamsField/AdditionalParamsField";
import { CandidateProfile, TField } from "./CandidateProfile/CandidateProfile";
import { Button } from "@src/shared/components/Button/Button";
import { useCreateRequestHH } from "@src/entities/requestHH/model/hooks/useCreateRequestHH/useCreateRequestHH";
import { TСreateRequestHHDto2 } from "@src/entities/requestHH/api/requests/createRequestHH";
import { useGetUserInfoMe } from "@src/entities/user";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserInfoMeStore } from "@src/entities/user/model/store/userInfoMeStore";
import { useEffect, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Autocomplete,
  Checkbox,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import bulbImg from "@src/shared/assets/bulb_img.svg";
import CloseIcon from "@mui/icons-material/Close";
import { StepHeader } from "@src/pages/creatingSearchRequestHH/ui/StepHeader/StepHeader";
import { InputField } from "@src/shared/ui/InputField";
import { useGetFilters } from "@src/entities/requestHH/model/hooks/useGetFilters/useGetFilters";
import { EditableListField } from "./EditableListField/EditableListField";

import styles from "./CreatingSearchHH.module.scss";
import { RequirementsDndLists } from "./RequirementsDndLists/RequirementsDndLists";

interface Props {
  defaultValue: CreatingSearchHHForm;
  onRequestCreated?: () => void;
}

export function CreatingSearchHH({ defaultValue, onRequestCreated }: Props) {
  const [levels, setLevels] = useState<
    {
      name: string;
      id: string;
    }[]
  >([]);
  const { mutate: createRequestHH, isPending } = useCreateRequestHH();
  const { refetch } = useGetUserInfoMe();
  const navigate = useNavigate();
  const user = useUserInfoMeStore((state) => state.user);
  const location = useLocation();
  const [isNeedChangeRequest, setIsNeedChangeRequest] =
    useState<boolean>(false);
  const targetRef = useRef<HTMLDivElement>(null);
  const { data: filters } = useGetFilters();
  const { grades } = filters?.data || {};
  const PROFILE_OPTIONS = grades?.systems || [];
  const gradesDictionary =
    grades?.levels?.reduce(
      (acc, item) => {
        if (!acc[item.system_id]) {
          acc[item.system_id] = item.levels;

          return acc;
        }
        return acc;
      },
      {} as Record<string, (typeof grades.levels)[0]["levels"]>,
    ) || {};

  const isChangeQuery = location.pathname.includes("/change-search-query");
  const isDisabledFields = isChangeQuery && !isNeedChangeRequest;

  const methods = useForm<CreatingSearchHHForm>({
    defaultValues: defaultValue,
    resolver: yupResolver(creatingSearchHHSchema) as any,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid, errors },
  } = methods;

  const profile = watch("profile");

  const experienceFrom: TField<CreatingSearchHHForm> = {
    name: "experienceFrom",
    placeholder: "От",
  };

  const experienceTo: TField<CreatingSearchHHForm> = {
    name: "experienceTo",
    placeholder: "До",
  };

  const educationField: TField<CreatingSearchHHForm> = {
    name: "education",
    placeholder: "Выберите образование",
  };

  const genderField: TField<CreatingSearchHHForm> = {
    name: "gender",
  };

  const ageFromField: TField<CreatingSearchHHForm> = {
    name: "ageFrom",
  };

  const ageToField: TField<CreatingSearchHHForm> = {
    name: "ageTo",
  };

  const withoutAgeField: TField<CreatingSearchHHForm> = {
    name: "isWithoutAge",
  };

  const isProfilePreferredField: TField<CreatingSearchHHForm> = {
    name: "isEduOptional",
  };

  const handleUnlockСhanges = () => setIsNeedChangeRequest(true);

  const onSubmit = (data: CreatingSearchHHForm) => {
    const params: TСreateRequestHHDto2 = {
      salary_to: getNumericalValue(data.salary_to),
      salary_from: getNumericalValue(data.salary_from),
      work_experience_from: getNumericalValue(data.experienceFrom),
      work_experience_to: getNumericalValue(data.experienceTo),
      age_from: getNumericalValue(data.ageFrom),
      age_to: getNumericalValue(data.ageTo),
      exclude_job_hoppers: data.isFrequentJobChanges ?? false,
      requires_car: data.isHasCar,
      requirements: {
        mandatory: data.mandatory?.length
          ? data.mandatory.map((el) => el.value).filter(Boolean)
          : [],
        priority: data.priority?.length
          ? data.priority.map((el) => el.value).filter(Boolean)
          : [],
        optional: data.optional?.length
          ? data.optional.map((el) => el.value).filter(Boolean)
          : [],
      },
      company_industry: data.companyIndustry ?? "",
      exclusion_criteria: data.exclusionCriteria?.length
        ? data.exclusionCriteria.map((el) => el.value).filter(Boolean)
        : [],
      location_ids: getAreaId(data.isRussia, ALL_REGIONS_ID, data.area),
      work_format_ids: data.workFormat?.length
        ? data.workFormat.map(({ id }) => id)
        : [],
      education_type: data.education?.name ?? null,
      gender_id: data.gender || null,
      grade_ids: {
        system_id: data.profile?.id ?? null,
        level_ids: data.specialistLevel?.length
          ? data.specialistLevel.map((el) => el.id)
          : [],
      },
      relocation_types_ids: data.resumeRelocation?.hh_id
        ? [data.resumeRelocation.hh_id]
        : [],
      match_without_salary: data.showWithout ?? false,
      job_search_status_ids: data.jobSearchStatus?.length
        ? data.jobSearchStatus.map((el) => el.hh_id)
        : [],
      match_without_age: data.isWithoutAge ?? false,
      rf_citizenship: data.citizenship ?? false,
      vacancy_name: data.vacancyName ?? "",
      grade_labels: {
        system_label: data.profile?.name ?? null,
        level_labels: data.specialistLevel?.length
          ? data.specialistLevel.map((el) => el.name)
          : [],
      },
      car_license: data.driverLicenseCategories ?? [],
      is_edu_optional: data.isEduOptional ?? false,
    };

    createRequestHH(params, {
      onSuccess: () => {
        onRequestCreated?.();
        refetch();
        setTimeout(() => {
          navigate("/");
        }, 0);
      },
    });
  };

  const skills = watch("skills");
  const isDisabledBtns = !skills;

  useEffect(() => {
    if (isDisabledBtns) {
      setValue("skillLogic", "and");
    }
  }, [skills]);

  useEffect(() => {
    if (targetRef.current && isChangeQuery) {
      targetRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, []);

  useEffect(() => {
    if (profile?.id) {
      setLevels(gradesDictionary[profile.id] || []);
      return;
    }

    setLevels([]);
    setValue("specialistLevel", []);
  }, [profile, setValue]);

  const isMustHaveSkillsError = !!errors?.mandatory;
  const isDisabledSubmitBtn = !isValid || isPending;
  const attemptsInfo = `Количество запросов: ${user?.last_license?.used_request ?? 0} /
          ${user?.last_license?.available_attempts ?? 0}`;

  return (
    <div className={styles.wrapper}>
      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.headerTop}>
            <h2 className={styles.formTitle}>Функциональные требования</h2>
            <StepHeader step={2} />
          </div>
          <div className={styles.leftBlock}>
            <div className={styles.header}>
              <div className={styles.title}>
                <span>Описание роли</span>
              </div>
              <div className={styles.rightBtn}></div>
            </div>
            <div className={styles.subTitle}>
              <span>Название вакансии:</span>
            </div>
            <InputField
              control={control}
              name="vacancyName"
              className={styles.inputField}
              classNameForWrapper={styles.inputFieldWrapper}
              disabled={true}
            />
            <div className={styles.selectsBlock}>
              <div className={styles.specialistLevelBlock}>
                <Controller
                  name={"profile"}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <div className={styles.gradeTitle}>
                        <span>Профиль:</span>
                      </div>

                      <Autocomplete
                        options={PROFILE_OPTIONS}
                        disabled={isDisabledFields}
                        className={styles.autocomplete}
                        value={value}
                        onChange={(_, newValue) => {
                          onChange(newValue);

                          setValue("specialistLevel", []);

                          if (newValue?.id) {
                            setLevels(gradesDictionary[newValue.id] || []);
                          } else {
                            setLevels([]);
                          }
                        }}
                        getOptionLabel={(option) =>
                          typeof option === "object" ? option.name : option
                        }
                        isOptionEqualToValue={(option, val) =>
                          option.id === val.id
                        }
                        clearIcon={crossImg}
                        popupIcon={openImg}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Выберите уровень специалиста"
                            variant="outlined"
                            className={styles.inputField}
                            sx={{
                              borderRadius: "40px",
                              "& input": {
                                color: "rgba(38, 38, 38, 1)",
                              },
                              "& .MuiOutlinedInput-root": {
                                background: "rgba(164, 187, 199, 0.25)",
                              },
                            }}
                          />
                        )}
                        sx={{
                          mt: 2,
                          ".MuiAutocomplete-popupIndicator": { color: "#fff" },
                          ".MuiSvgIcon-root": { color: "#fff" },
                        }}
                      />
                    </>
                  )}
                />
              </div>
              <div className={styles.gradeBlock}>
                <Controller
                  name={"specialistLevel"}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <div className={styles.gradeTitle}>
                        <span>Уровень специалиста:</span>
                      </div>
                      <Autocomplete
                        multiple
                        options={levels}
                        disabled={isDisabledFields}
                        className={styles.autocomplete}
                        disableCloseOnSelect
                        clearIcon={crossImg}
                        popupIcon={openImg}
                        getOptionLabel={(option) => {
                          return typeof option === "object"
                            ? option.name
                            : option;
                        }}
                        isOptionEqualToValue={(option, val) =>
                          option.id === val.id
                        }
                        value={value}
                        onChange={(_, newValue) => onChange(newValue)}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={`${option.name}_${option.id}`}>
                            <Checkbox
                              style={{ marginRight: 8 }}
                              checked={selected}
                              key={option.id}
                              disableRipple
                            />
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={"Выберите грейды"}
                            variant="outlined"
                            className={styles.inputField}
                            sx={{
                              borderRadius: "40px",
                              "& input": {
                                color: "rgba(38, 38, 38, 1)",

                                "&:-webkit-autofill": {
                                  WebkitTextFillColor: "#fff",
                                  caretColor: "rgba(38, 38, 38, 1)",
                                  transition:
                                    "background-color 5000s ease-in-out 0s",
                                },
                              },
                              "& .MuiOutlinedInput-root": {
                                alignItems: "center",
                                alignContent: "center",
                                display: "flex",
                                "& fieldset": {
                                  borderColor: "rgba(164, 187, 199, 1)",
                                  background: "rgba(164, 187, 199, 0.25)",
                                },
                                "&.Mui-disabled:hover fieldset": {
                                  borderColor: "currentcolor",
                                },
                                "&:hover fieldset": {
                                  borderColor: "rgba(164, 187, 199, 1)",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "rgba(164, 187, 199, 1)",
                                },
                                "& .MuiOutlinedInput-input.Mui-disabled": {
                                  WebkitTextFillColor:
                                    "rgba(255, 255, 255, 0.7)",
                                },
                              },
                              ".MuiFormHelperText-root.Mui-error": {
                                color: "rgba(255, 255, 255, 0.8)",
                              },
                              ".MuiFormHelperText-root.Mui-disabled": {
                                color: "rgba(255, 255, 255, 0.8)",
                                fontWeight: 400,
                              },
                            }}
                          />
                        )}
                        sx={{
                          mt: 2,
                          ".MuiAutocomplete-popupIndicator": { color: "#fff" },
                          ".MuiSvgIcon-root": { color: "#fff" },
                          color: "#fff",
                        }}
                      />
                    </>
                  )}
                />
              </div>
            </div>
            <div className={styles.subTitle}>
              <span>Отрасль:</span>
            </div>
            <InputField
              control={control}
              name="companyIndustry"
              placeholder="Вставьте описание"
              className={styles.inputField}
              classNameForWrapper={styles.inputFieldWrapper}
              disabled={isDisabledFields}
            />
            <RequirementsDndLists
              control={control}
              isDisabled={isDisabledFields}
              mandatoryError={
                isMustHaveSkillsError
                  ? String(errors.mandatory?.message)
                  : undefined
              }
            />
            <EditableListField
              control={control}
              name="exclusionCriteria"
              title="Исключающие требования:"
              placeholder="Вставьте описание"
              isDisabled={isDisabledFields}
            />
          </div>
          <div className={styles.rightBlock}>
            <div className={styles.rightBlockContent}>
              <CandidateProfile
                control={control}
                field1={experienceFrom}
                field2={educationField}
                field3={genderField}
                field4={ageFromField}
                field5={ageToField}
                field6={withoutAgeField}
                field7={experienceTo}
                field8={isProfilePreferredField}
                isDisabledBtns={isDisabledBtns}
                isDisabledField={isDisabledFields}
              />
              <SalaryRangeAccordion isDisabledFields={isDisabledFields} />
              <SearchRegionField
                nameRegionAutocomplete="area"
                nameRegionCheckbox="isRussia"
                nameWorkFormat="workFormat"
                control={control}
                placeholder="Поиск региона..."
                resumeRelocationName="resumeRelocation"
                isDisabledFields={isDisabledFields}
                watch={watch}
                setValue={setValue}
              />
              <AdditionalParamsField
                citizenshipName="citizenship"
                nameProfEducation="isProfEducation"
                nameFrequentChange="isFrequentChange"
                isHasCar="isHasCar"
                educationName="education"
                isFrequentJobChanges="isFrequentJobChanges"
                jobStatusName="jobSearchStatus"
                isWithoutAge="isWithoutAge"
                isDisabledFields={isDisabledFields}
                control={control}
              />
            </div>
          </div>
          <div className={styles.footer}>
            <div className={styles.requestInfo}>
              <div className={styles.requestCount}>
                <span>{attemptsInfo}</span>
              </div>
              <div className={styles.button}>
                {isNeedChangeRequest || !isChangeQuery ? (
                  <Button
                    btnText="Создать запрос"
                    typeBtn="submit"
                    isDisabled={isDisabledSubmitBtn}
                  />
                ) : (
                  <div className={styles.infoPanel} ref={targetRef}>
                    <div className={styles.infoTextWrapper}>
                      <img src={bulbImg} />
                      <p className={styles.infoText}>
                        Вы можете отредактировать и повторить запрос. Будьте
                        внимательны, повторный запрос приравнивается к новому
                        (т.е. количество доступных запросов уменьшается).
                      </p>
                    </div>
                    <div className={styles.btns}>
                      <Button
                        btnText="Редактировать"
                        typeBtn="button"
                        colorVariant="primary"
                        onClick={handleUnlockСhanges}
                      />
                      <Button
                        btnText="Повторить запрос"
                        typeBtn="button"
                        colorVariant="primary"
                        onClick={handleSubmit(onSubmit)}
                      />
                      <Tooltip
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: "#fff",
                              color: "#000",
                              boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
                              borderRadius: "8px",
                              padding: "10px 12px",
                              maxWidth: "260px",
                            },
                          },
                          arrow: {
                            sx: {
                              color: "#fff",
                            },
                          },
                        }}
                        title={
                          <div className={styles.tooltipContent}>
                            <img
                              src={bulbImg}
                              alt=""
                              className={styles.tooltipIcon}
                            />

                            <div className={styles.tooltipText}>
                              <div className={styles.tooltipTitle}>
                                Закрыть форму
                              </div>
                              <div className={styles.tooltipSubtitle}>
                                без редактирования
                              </div>
                            </div>
                          </div>
                        }
                      >
                        <div className={styles.crossWrapper}>
                          <IconButton onClick={() => navigate(-1)}>
                            <CloseIcon
                              sx={{
                                width: "20px",
                                height: "20px",
                                color: "rgba(107, 104, 104, 0.76)",
                              }}
                            />
                          </IconButton>
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
